import {
  AT_HOME_SITUATION,
  DOCS_FORM_NAMES,
  HOME_CARE_SITUATION,
  LACK_OF_CAPACITY_DOCUMENT,
  LEGAL_GUARDIAN,
  MUSCOSKELETAL_RESILIENCE,
  OUTPATIENT_NOT_POSSIBLE_REASONS,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_NOT_APPLICABLE,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  REHAB_TRIGGERING_EVENT,
  REQUESTED_REHAB_SERVICE,
  REQUESTED_REHAB_SERVICE_TYPE,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import { formatUnixDate, parseDateStringToUnix } from "core/model/utils/dates";
import { ONTOLOGIES, OntologyType } from "core/model/utils/ontologies";
import {
  AtHomeSituation,
  DocsFormDataMapped,
  DocsFormName,
  GetOntologyType,
  HomeCareSituation,
  LackOfCapacity,
  LegalGuardian,
  MuskuloskeletalResilience,
  OutPatientNotPossibleReason,
  PredicamentState,
  RehabAdmission,
  RehabFocus,
  RehabTriggeringEvent,
  RequestedRehaService,
  RequestedRehaServiceType,
  TransportSubtype,
  TransportType,
  TypeOfRehab,
  ValueOf,
} from "core/types";
import Translations from "translations/types";

type ContradictionValueToHumanReadableProps<T extends DocsFormName> = {
  getOntology: GetOntologyType;
  value: ValueOf<DocsFormDataMapped<T>>;
  fieldName: keyof DocsFormDataMapped<T>;
  translations: Translations;
};

export type ContradictionValueToHumanReadable<T extends DocsFormName> = ({
  fieldName,
  getOntology,
  translations,
  value,
}: ContradictionValueToHumanReadableProps<T>) => string;

type ContradictionValueToHumanReadableConfig<T extends DocsFormName> = Record<
  keyof DocsFormDataMapped<T>,
  ContradictionValueToHumanReadable<T>
>;

type FormNameToContradictionValueToHumanReadable = {
  [T in DocsFormName]: ContradictionValueToHumanReadableConfig<T>;
};

const passThrough =
  <T extends DocsFormName>(
    _formName: T,
    getFieldLabel: (translations: Translations) => string,
  ) =>
  ({
    translations,
    value,
  }: ContradictionValueToHumanReadableProps<T>): string =>
    `${getFieldLabel(translations)}: ${JSON.stringify(value ?? "")}`;

export const toDateString =
  <T extends DocsFormName>(
    _formName: T,
    getFieldLabel: (translations: Translations) => string,
  ) =>
  ({
    translations,
    value,
  }: ContradictionValueToHumanReadableProps<T>): string => {
    const unix =
      typeof value === "string"
        ? parseDateStringToUnix(value)
        : typeof value === "number" && Number.isFinite(value)
        ? value
        : undefined;

    const formatted =
      unix != null ? formatUnixDate(unix, "de") : JSON.stringify(value);

    return `${getFieldLabel(translations)}: ${formatted}`;
  };

const toOntologyString =
  <T extends DocsFormName>(
    _formName: T,
    ontologyType: OntologyType,
    getFieldLabel: (translations: Translations) => string,
  ) =>
  ({
    getOntology,
    translations,
    value,
  }: ContradictionValueToHumanReadableProps<T>) => {
    const ontologyString = getOntology({
      key: value as number,
      type: ontologyType,
    });
    return `${getFieldLabel(translations)}: ${ontologyString}`;
  };

const toOntologyListString =
  <T extends DocsFormName>(
    _formName: T,
    ontologyType: OntologyType,
    getFieldLabel: (translations: Translations) => string,
  ) =>
  ({
    getOntology,
    translations,
    value,
  }: ContradictionValueToHumanReadableProps<T>) => {
    const ids = Array.isArray(value) ? value : value == null ? [] : [value];
    const parts = (ids as number[]).map((key) =>
      getOntology({ key, type: ontologyType }),
    );
    return `${getFieldLabel(translations)}: ${parts.join(", ")}`;
  };

const toTranslatedEnum =
  <T extends DocsFormName>(
    _formName: T,
    getFieldLabel: (translations: Translations) => string,
    getValueLabel: (value: unknown, translations: Translations) => string,
  ) =>
  ({
    translations,
    value,
  }: ContradictionValueToHumanReadableProps<T>): string => {
    const valueStr = getValueLabel(value, translations);
    return `${getFieldLabel(translations)}: ${valueStr}`;
  };

const getLivingSituationValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<HomeCareSituation, string> = {
    [HOME_CARE_SITUATION.ASSISTED_LIVING]:
      translations.patientForms.generalForm.sectionI.optionAssistedLiving,
    [HOME_CARE_SITUATION.CARE_HOME]:
      translations.patientForms.generalForm.sectionI.optionCareHome,
    [HOME_CARE_SITUATION.HOME]:
      translations.patientForms.generalForm.sectionI.optionAtHome,
  };
  return map[value as HomeCareSituation] ?? String(value);
};

const getPredicamentStateValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<PredicamentState, string> = {
    [PREDICAMENT_STATE_NO]: translations.general.no,
    [PREDICAMENT_STATE_NOT_APPLICABLE]:
      translations.patientForms.drvMedicalForm.sectionZero.notApplicable,
    [PREDICAMENT_STATE_UNKNOWN]: translations.general.unknown,
    [PREDICAMENT_STATE_YES]: translations.actions.yes,
  };
  return map[value as PredicamentState] ?? String(value);
};

const getRehabTriggeringEventValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<RehabTriggeringEvent, string> = {
    [REHAB_TRIGGERING_EVENT.ACCIDENT_WORK]:
      translations.patientForms.drvMedicalForm.sectionZero.accidentWork,
    [REHAB_TRIGGERING_EVENT.ACUTE_EVENT]:
      translations.patientForms.drvMedicalForm.sectionZero.acuteEvent,
    [REHAB_TRIGGERING_EVENT.ANTINEOPLASTIC_THERAPY]:
      translations.patientForms.drvMedicalForm.sectionZero
        .antineoplasticTherapy,
    [REHAB_TRIGGERING_EVENT.NOT_APPLICABLE]:
      translations.patientForms.drvMedicalForm.sectionZero.notApplicable,
    [REHAB_TRIGGERING_EVENT.RADIATION]:
      translations.patientForms.drvMedicalForm.sectionZero.radiation,
    [REHAB_TRIGGERING_EVENT.SURGERY]:
      translations.patientForms.drvMedicalForm.sectionZero.surgery,
    [REHAB_TRIGGERING_EVENT.THIRD_PARTY_DAMAGE]:
      translations.patientForms.drvMedicalForm.sectionZero.thirdPartyDamage,
  };
  const ids = Array.isArray(value) ? value : [value];
  return ids
    .map((id) => map[id as RehabTriggeringEvent] ?? String(id))
    .join(", ");
};

const getOutpatientNotPossibleReasonValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<OutPatientNotPossibleReason, string> = {
    [OUTPATIENT_NOT_POSSIBLE_REASONS.ACCOMPANYING_PERSON]:
      translations.patientForms.drvMedicalForm.sectionFive.accompanyingPerson,
    [OUTPATIENT_NOT_POSSIBLE_REASONS.AMBULANCE]:
      translations.patientForms.drvMedicalForm.sectionFive.lyingDown,
    [OUTPATIENT_NOT_POSSIBLE_REASONS.TAXI]:
      translations.patientForms.drvMedicalForm.sectionFive.taxi,
  };
  const ids = Array.isArray(value) ? value : [value];
  return ids
    .map((id) => map[id as OutPatientNotPossibleReason] ?? String(id))
    .join(", ");
};

const getLivingSituationAtHomeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<AtHomeSituation, string> = {
    [AT_HOME_SITUATION.ALONE]:
      translations.patientForms.generalForm.sectionI.optionAtHomeAlone,
    [AT_HOME_SITUATION.RELATIVES]:
      translations.patientForms.generalForm.sectionI.optionAtHomeWithRelatives,
  };
  return map[value as AtHomeSituation] ?? String(value);
};

const getLegalGuardianTypeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<LegalGuardian, string> = {
    [LEGAL_GUARDIAN.AUTHORIZED]:
      translations.patientForms.drvGeneralForm.sectionEleven
        .authorisedRepresentative,
    [LEGAL_GUARDIAN.CUSTODIAN]:
      translations.patientForms.drvGeneralForm.sectionEleven.guardian,
    [LEGAL_GUARDIAN.STATUTORY_REP]:
      translations.patientForms.drvGeneralForm.sectionEleven.legalRep,
    [LEGAL_GUARDIAN.SUPERVISOR]:
      translations.patientForms.drvGeneralForm.sectionEleven.carer,
  };
  return map[value as LegalGuardian] ?? String(value);
};

const getConsentOfTheInsuredValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<LackOfCapacity, string> = {
    [LACK_OF_CAPACITY_DOCUMENT.ATTACHED]:
      translations.patientForms.drvGeneralForm.sectionEleven.attached,
    [LACK_OF_CAPACITY_DOCUMENT.SEND_LATER]:
      translations.patientForms.drvGeneralForm.sectionEleven.submittedLater,
  };
  return map[value as LackOfCapacity] ?? String(value);
};

const getRequestedRehabServiceValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<RequestedRehaService, string> = {
    [REQUESTED_REHAB_SERVICE.INSURED]:
      translations.patientForms.drvGeneralForm.sectionOne.rehabForInsured,
    [REQUESTED_REHAB_SERVICE.RELATIVES]:
      translations.patientForms.drvGeneralForm.sectionOne.oncologicalRehab,
  };
  return map[value as RequestedRehaService] ?? String(value);
};

const getRequestedRehabServiceTypeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<RequestedRehaServiceType, string> = {
    [REQUESTED_REHAB_SERVICE_TYPE.INPATIENT]:
      translations.patientForms.drvGeneralForm.sectionOne.inpatient,
    [REQUESTED_REHAB_SERVICE_TYPE.OUTPATIENT]:
      translations.patientForms.drvGeneralForm.sectionOne.outpatient,
  };
  return map[value as RequestedRehaServiceType] ?? String(value);
};

const getRehabAdmissionValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<RehabAdmission, string> = {
    [REHAB_ADMISSION.DIRECT]:
      translations.patientForms.medicalForm.sectionOne.optionDirect,
    [REHAB_ADMISSION.OUTSIDE_TWO_WEEKS]:
      translations.patientForms.medicalForm.sectionOne.outsideTwoWeeks,
    [REHAB_ADMISSION.WITHIN_TWO_WEEKS]:
      translations.patientForms.medicalForm.sectionOne.withinTwoWeeks,
  };
  return map[value as RehabAdmission] ?? String(value);
};

const getTypeOfRehabValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<TypeOfRehab, string> = {
    [TYPE_OF_REHAB.AMBULANT]:
      translations.patientForms.medicalForm.sectionFive.mobile,
    [TYPE_OF_REHAB.AMBULANT_MOBIL]:
      translations.patientForms.medicalForm.sectionFive.mobileOutpatient,
    [TYPE_OF_REHAB.STATIONARY]:
      translations.patientForms.medicalForm.sectionFive.static,
  };
  return map[value as TypeOfRehab] ?? String(value);
};

const getRehaCapacityValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<MuskuloskeletalResilience, string> = {
    [MUSCOSKELETAL_RESILIENCE.FULLY]:
      translations.patientForms.medicalForm.sectionThree.fully,
    [MUSCOSKELETAL_RESILIENCE.PARTIALLY]:
      translations.patientForms.medicalForm.sectionThree.partially,
    [MUSCOSKELETAL_RESILIENCE.STABLE]:
      translations.patientForms.medicalForm.sectionThree.stable,
  };
  return map[value as MuskuloskeletalResilience] ?? String(value);
};

const getRehabDirectiveTypeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null || value === "") return "";
  const map: Partial<Record<string, string>> = {
    [REHAB_DIRECTIVE_TYPE.BYPASS_WITH_VALVE_REPLACEMENT]:
      translations.patientForms.medicalForm.sectionFive.bypass,
    [REHAB_DIRECTIVE_TYPE.CHRONIC_BACK_PAIN]:
      translations.patientForms.medicalForm.sectionFive.backPain,
    [REHAB_DIRECTIVE_TYPE.ENDOPROSTHETIC_KNEE_OR_HIP]:
      translations.patientForms.medicalForm.sectionFive.endoprostheticTreatment,
    [REHAB_DIRECTIVE_TYPE.GERIATRIC_REHAB]:
      translations.patientForms.medicalForm.sectionFive.geriatricRehab,
    [REHAB_DIRECTIVE_TYPE.HEART_FAILURE_NYHA_II_OR_HIGHER]:
      translations.patientForms.medicalForm.sectionFive.heartFailure,
    [REHAB_DIRECTIVE_TYPE.MAJOR_LEG_AMPUTATION]:
      translations.patientForms.medicalForm.sectionFive.amputation,
    [REHAB_DIRECTIVE_TYPE.MYOCARDIAL_INFARCTION]:
      translations.patientForms.medicalForm.sectionFive.myocardialInfarction,
    [REHAB_DIRECTIVE_TYPE.NEUROLOGICAL_DISEASE_PHASE_D]:
      translations.patientForms.medicalForm.sectionFive.neurologicalDiseases,
    [REHAB_DIRECTIVE_TYPE.ONCOLOGICAL_DISEASES]:
      translations.patientForms.medicalForm.sectionFive.oncologicalDisease,
    [REHAB_DIRECTIVE_TYPE.ORGAN_TRANSPLANTS]:
      translations.patientForms.medicalForm.sectionFive.transplants,
    [REHAB_DIRECTIVE_TYPE.RESPIRATORY_DISEASES]:
      translations.patientForms.medicalForm.sectionFive.respiratoryDisease,
    [REHAB_DIRECTIVE_TYPE.SPINAL_STENOSIS]:
      translations.patientForms.medicalForm.sectionFive.spinalCanal,
  };
  return map[String(value)] ?? String(value);
};

const getRehabFocusValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<RehabFocus, string> = {
    [REHAB_FOCUS.ADDICTIVE_DISORDER]:
      translations.patientForms.medicalForm.sectionFive.addictiveDisorders,
    [REHAB_FOCUS.BLOOD_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.bloodDiseases,
    [REHAB_FOCUS.BLOOD_VESSELS]:
      translations.patientForms.medicalForm.sectionFive.bloodVessels,
    [REHAB_FOCUS.ENDOCRINE_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.endocrineDiseases,
    [REHAB_FOCUS.GASTROENTEROLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive
        .gastroenterologicalDiseases,
    [REHAB_FOCUS.GERIATRICS]:
      translations.patientForms.medicalForm.sectionFive.geriatrics,
    [REHAB_FOCUS.GYNAECOLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.gynaecologicalDiseases,
    [REHAB_FOCUS.HEART]:
      translations.patientForms.medicalForm.sectionFive.heart,
    [REHAB_FOCUS.INFLAMMATORY_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.inflammatoryDiseases,
    [REHAB_FOCUS.MENTAL_ILLNESS]:
      translations.patientForms.medicalForm.sectionFive.mentalIllnesses,
    [REHAB_FOCUS.MUSCOLOSKELETAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.musculoskeletalSystem,
    [REHAB_FOCUS.NEPHROLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.nephrologicalDiseases,
    [REHAB_FOCUS.NEUROLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.neurologicalDisease,
    [REHAB_FOCUS.ONCOLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.oncologicalDiseases,
    [REHAB_FOCUS.OTHER]:
      translations.patientForms.medicalForm.sectionFive.others,
    [REHAB_FOCUS.PSYCHOSOMATIC_DISORDER]:
      translations.patientForms.medicalForm.sectionFive.psychosomaticDisorders,
    [REHAB_FOCUS.RESPIRATORY_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.respiratoryDiseases,
    [REHAB_FOCUS.SKIN_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.skinDiseases,
    [REHAB_FOCUS.UROLOGICAL_DISEASE]:
      translations.patientForms.medicalForm.sectionFive.urologicalDiseases,
  };
  return map[value as RehabFocus] ?? String(value);
};

const getTransportTypeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<TransportType, string> = {
    [TRANSPORTATION_TYPE.AMBULANCE]:
      translations.patientForms.medicalForm.sectionSix.ambulance,
    [TRANSPORTATION_TYPE.CAR]:
      translations.patientForms.medicalForm.sectionSix.car,
    [TRANSPORTATION_TYPE.MOBILE_REHAB]:
      translations.patientForms.medicalForm.sectionSix.mobileRehab,
    [TRANSPORTATION_TYPE.PUBLIC]:
      translations.patientForms.medicalForm.sectionSix.optionPublicTransport,
    [TRANSPORTATION_TYPE.TAXI]:
      translations.patientForms.medicalForm.sectionSix.taxi,
  };
  return map[value as TransportType] ?? String(value);
};

const getTransportSubtypeValueLabel = (
  value: unknown,
  translations: Translations,
): string => {
  if (value == null) return "";
  const map: Record<TransportSubtype, string> = {
    [TRANSPORTATION_SUBTYPE.LYING_DOWN]:
      translations.patientForms.medicalForm.sectionSix.lyingDown,
    [TRANSPORTATION_SUBTYPE.STRETCHER]:
      translations.patientForms.medicalForm.sectionSix.stretcher,
    [TRANSPORTATION_SUBTYPE.WHEELCHAIR]:
      translations.patientForms.medicalForm.sectionSix.wheelChair,
  };
  return map[value as TransportSubtype] ?? String(value);
};

// the label translations currently use the labels from the forms
// they should use ideally the labels in the patient profile where the information came
// this is a temporary solution, until we have a better design for this
export const FORM_NAME_TO_CONTRADICTION_VALUE_CONFIG: FormNameToContradictionValueToHumanReadable =
  {
    [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]: {
      additional_diagnostic_findings: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .furtherDiagnosisLabel,
      ),
      allergies: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology.allergyLabel,
      ),
      anamnesis: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .medicalHistoryLabel,
      ),
      diagnoses: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .diagnosisLabel,
      ),
      discharge_medications: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .dischargeMedicationsLabel,
      ),
      ecg: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology.ecgLabel,
      ),
      physical_status: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .physicalStatusLabel,
      ),
      previous_diagnoses: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .previousDiagnosesLabel,
      ),
      social_history: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .socialHistoryLabel,
      ),
      therapy_and_course: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formCardiology
            .therapyAndCourseLabel,
      ),
    },
    [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: {
      additional_findings: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.futherFindingsLabel,
      ),
      admission_medications: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt
            .medicationUponAdmission,
      ),
      anamnesis: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.medicalHistoryLabel,
      ),
      clinical_course: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.historyLabel,
      ),
      diagnoses: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.diagnosisLabel,
      ),
      discharge_medications: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt
            .dischargeMedicationLabel,
      ),
      findings: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.findingsLabel,
      ),
      laboratory_parameters: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.laboratoryParameters,
      ),
      recommendations: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.recommendation,
      ),
      secondary_diagnoses: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.secondaryDiagnoses,
      ),
      therapy: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
        (translations) =>
          translations.extractDocs.dischargeLetter.formEnt.therapyLabel,
      ),
    },
    [DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY]: {
      discharge_recommendations: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .dischargeRecommendationLabel,
      ),
      epicrisis: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .epicrisisLabel,
      ),
      imaging: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .imagingLabel,
      ),
      lab_results: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .labResultsLabel,
      ),
      main_diagnosis: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .diagnosisLabel,
      ),
      medication_upon_discharge: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .dischargeMedicationLabel,
      ),
      secondary_diagnosis: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .secondaryDiagnosisLabel,
      ),
      therapy: passThrough(
        DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY,
        (translations) =>
          translations.extractDocs.dischargeLetter.formTraumSurgery
            .therapyLabel,
      ),
    },
    [DOCS_FORM_NAMES.DRV_GENERAL]: {
      application_submitted: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.submittedWhere,
      ),
      application_submitted_applicable: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .submittedApplication,
      ),
      before_retirement_pension: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.benefitsType,
      ),
      before_retirement_pension_applicable: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .pensionOldAgeBenefits,
      ),
      care_person_necessary: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThreeNew.subtitle,
      ),
      claim_for_damages_approved: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.damageClaims,
      ),
      claim_for_damages_description: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.compensatedOn,
      ),
      claim_for_damages_file_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.fileNumber,
      ),
      claim_for_damages_origin: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .compensationClaims,
      ),
      consent_of_the_insured: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.lackOfCapacity,
        getConsentOfTheInsuredValueLabel,
      ),
      contact_guardian: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.nameRep,
      ),
      contributions_to_foreign_insurance_current: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEight
            .currentContributionsAbroad,
      ),
      contributions_to_foreign_insurance_info: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEight
            .contributionsAbroad,
      ),
      contributions_to_foreign_insurance_info_from_to: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEight.timeline,
      ),
      contributions_to_foreign_insurance_info_state: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEight
            .contributionsState,
      ),
      contributions_to_statutory_pension: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEight
            .contributionsPension,
      ),
      doctor_in_charge_in_hospital: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSix.doctorName,
      ),
      doctor_in_charge_in_hospital_first_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.doctorFirstName,
      ),
      doctor_in_charge_in_hospital_phone: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSeven.doctorPhone,
      ),
      employment_status: toOntologyString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        ONTOLOGIES.PRIOR_EMPLOYMENT_STATUS,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionFour.subtitle,
      ),
      gender_extended: toOntologyString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        ONTOLOGIES.GENDER_EXTENDED,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.gender,
      ),
      health_damage_file_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.fileNumber,
      ),
      health_damage_origin: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.recognisedBy,
      ),
      health_damage_present: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .workRelatedAccident,
      ),
      health_damages_description: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.healthIssues,
      ),
      hospital_name_address: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .addressHospital,
      ),
      insurance_company_name_address: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .addressHealthInsurance,
      ),
      insurance_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSix.insuranceName,
      ),
      insurance_type: toOntologyString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        ONTOLOGIES.INSURANCE_TYPE,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSix.patientInsurance,
      ),
      jobcenter_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionNine.jobCenter,
      ),
      last_employment: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThree
            .fieldLabelProfession,
      ),
      legal_guardian_email: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.email,
      ),
      legal_guardian_house_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.street,
      ),
      legal_guardian_phone: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.phone,
      ),
      legal_guardian_town: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.city,
      ),
      legal_guardian_type: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven
            .legalGuardianType,
        getLegalGuardianTypeValueLabel,
      ),
      legal_guardian_zip_code: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.zipCode,
      ),
      marital_status: toOntologyString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        ONTOLOGIES.MARITAL_STATUS,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThree.subtitle,
      ),
      needs_disability_equipment: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwelev
            .descriptionAids,
      ),
      needs_disability_equipment_applicable: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwelev.aids,
      ),
      patient_bank_account_holder: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.accountHolder,
      ),
      patient_bank_iban: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.iban,
      ),
      patient_bank_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.bankName,
      ),
      patient_birth_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.maidenName,
      ),
      patient_birthdate: toDateString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.dateBirthPatient,
      ),
      patient_city: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.city,
      ),
      patient_first_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.firstName,
      ),
      patient_last_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.lastName,
      ),
      patient_maiden_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.formerNames,
      ),
      patient_nationality: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.nationality,
      ),
      patient_phone_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.phone,
      ),
      patient_street_housenumber: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.street,
      ),
      patient_unable_to_work_before_hospitalization: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSix
            .subtitleHospitalStay,
      ),
      patient_unable_to_work_before_hospitalization_date: toDateString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSix.sinceDate,
      ),
      patient_zipcode: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.zipCode,
      ),
      pension_insurance_company_name: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .addressPensionInsurance,
      ),
      postal_code_doctor: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSeven.doctorZipCode,
      ),
      preferred_rehab_clinic: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .informationAhbFacility,
      ),
      prior_employment_status: toOntologyString(
        DOCS_FORM_NAMES.DRV_GENERAL,
        ONTOLOGIES.PRIOR_EMPLOYMENT_STATUS,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionFive.title,
      ),
      requested_rehab_service: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionOne.title,
        getRequestedRehabServiceValueLabel,
      ),
      requested_rehab_service_type_insured: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionOne.rehabForInsured,
        getRequestedRehabServiceTypeValueLabel,
      ),
      requested_rehab_service_type_relatives: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionOne.oncologicalRehab,
        getRequestedRehabServiceTypeValueLabel,
      ),
      retirement_pension_received: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .namePensionInsurance,
      ),
      retirement_pension_received_applicable: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.pensionBenefits,
      ),
      risk_of_incapacity_caused_by_accident_third_person: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen.accidentOrPerson,
      ),
      send_documents_per_email: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThirteen.confirmation,
      ),
      send_documents_per_email_description: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThirteen
            .germanMailAddress,
      ),
      social_worker_name_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .informationSocialWorker,
      ),
      special_payments_from_civil_services: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTen
            .pensionInformation,
      ),
      statutory_pension_insurance_number: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.insuranceInformation
            .pensionInsuranceNumber,
      ),
      street_house_number_doctor: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSeven.doctorStreet,
      ),
      structured_treatment_plan: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSeven
            .remarksDescription,
      ),
      third_party_request: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionEleven.subtitle,
      ),
      town_doctor: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionSeven.doctorCity,
      ),
      unemployment_benefit_type_2: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionNine.subtitle,
      ),
      visually_impaired_document: passThrough(
        DOCS_FORM_NAMES.DRV_GENERAL,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionThirteen
            .additionalFormat,
      ),
    },
    [DOCS_FORM_NAMES.DRV_MEDICAL]: {
      admission_date: toDateString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.intakeDate,
      ),
      carelevel: toOntologyString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        ONTOLOGIES.CARE_LEVEL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive
            .carelevelDescription,
      ),
      carelevel_selected: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.carelevelLabel,
        getPredicamentStateValueLabel,
      ),
      clinical_findings: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.descriptionHistory,
      ),
      complications: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo
            .descriptionComplications,
      ),
      current_therapy: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo
            .descriptionTherapies,
      ),
      day_reha: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.mobile,
      ),
      discharge_date: toDateString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.dischargeDate,
      ),
      drg_code: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionOne.drgGroup,
      ),
      drv_remarks: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSeven
            .remarksDescription,
      ),
      eat_without_help: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.eat,
        getPredicamentStateValueLabel,
      ),
      fecal_incontinence: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.incontinence,
        getPredicamentStateValueLabel,
      ),
      full_reha: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.static,
      ),
      further_therapeutic_diagnostic_measures_description: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo
            .descriptionMeasures,
      ),
      further_therapeutic_diagnostic_measures_finished_on: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo.endsOnDate,
      ),
      further_therapeutic_diagnostic_measures_from: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo.fromDate,
      ),
      further_therapeutic_diagnostic_measures_to: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo.untilDate,
      ),
      hospital_bank_holder: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.accountHolder,
      ),
      hospital_bank_name: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.bankName,
      ),
      hospital_billing_address: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.address,
      ),
      hospital_iban: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.iban,
      ),
      hospital_ik_number: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix.ikNumber,
      ),
      hospital_name_address: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.addressHospital,
      ),
      icd_code_1_code: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionOne.diagnosisIcd,
      ),
      icd_code_2_code: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionOne.diagnosisIcd,
      ),
      icd_code_3_code: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionOne.diagnosisIcd,
      ),
      infections: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.germsDescription,
      ),
      infections_selected: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.germs,
        getPredicamentStateValueLabel,
      ),
      insurance_delivery_address: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionSix
            .addressPensionInsurance,
      ),
      main_diagnosis: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionOne
            .descriptionDiagnosis,
      ),
      mobility_walk_without_help: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.move,
        getPredicamentStateValueLabel,
      ),
      mobility_wheel_chair: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.wheelchair,
        getPredicamentStateValueLabel,
      ),
      needs_catheter: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.catheters,
      ),
      needs_catheter_selected: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive
            .cathetersDescription,
        getPredicamentStateValueLabel,
      ),
      orientation_disoriented: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.orientation,
      ),
      orientation_disoriented_selected: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive
            .orientationDescription,
        getPredicamentStateValueLabel,
      ),
      other_measures: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionTwo.otherMeasures,
        getPredicamentStateValueLabel,
      ),
      outpatient_not_possible_reason: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.medicalReason,
        getOutpatientNotPossibleReasonValueLabel,
      ),
      patient_birthdate: toDateString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.dateBirthPatient,
      ),
      patient_city: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.city,
      ),
      patient_height: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFour.height,
      ),
      patient_name: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.patientName,
      ),
      patient_street_housenumber: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.addressPatient,
      ),
      patient_weight: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFour.weight,
      ),
      patient_zipcode: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.zipCodePatient,
      ),
      pension_procedure_after_examination: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero
            .instructionAfterExamination,
      ),
      pension_procedure_direct: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.directDischarge,
      ),
      preferred_rehab_clinic: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.addressAhb,
      ),
      rehab_triggering_event: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero
            .ahbEventCheckboxGroupLabel,
        getRehabTriggeringEventValueLabel,
      ),
      rehab_triggering_event_date: toDateString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.ahbEventDate,
      ),
      secondary_diagnosis: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionThree.nyhaStages,
      ),
      secondary_diagnosis_selected: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionThree
            .descriptionIllnessess,
        getPredicamentStateValueLabel,
      ),
      social_worker_name_number: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero
            .contactPersonInformation,
      ),
      statutory_pension_insurance_number: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.insuranceNumber,
      ),
      transfer_date: toDateString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionZero.ahbStartDate,
      ),
      transport_type: toOntologyListString(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        ONTOLOGIES.TRANSPORT_TYPE,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.travel,
      ),
      urinary_incontinence: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive
            .urinalIncontinence,
        getPredicamentStateValueLabel,
      ),
      wash_and_dress_without_help: toTranslatedEnum(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.wash,
        getPredicamentStateValueLabel,
      ),
      wound_closed: passThrough(
        DOCS_FORM_NAMES.DRV_MEDICAL,
        (translations) =>
          translations.patientForms.drvMedicalForm.sectionFive.wound,
      ),
    },
    [DOCS_FORM_NAMES.GKV_GENERAL]: {
      case_id: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.patientId,
      ),
      communication_in_german_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo
            .communicationWithPatient,
      ),
      communication_other_language: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo
            .communicationNoGerman,
      ),
      data_transfer_consent_given: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .consentToDataTransferSignature,
      ),
      declaration_of_intent_confirmed: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.signatureDeclaration,
      ),
      domestic_situation_is_safe: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.currentHomeCareEnsured,
      ),
      general_practitioner_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.patientForms.generalForm.sectionI.gpCity,
      ),
      general_practitioner_contact_details: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.gpPhoneNumber,
      ),
      general_practitioner_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.firstName,
      ),
      general_practitioner_honorific_prefix: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patient.patientInformation
            .generalPractitionerHonorificPrefix,
      ),
      general_practitioner_honorific_suffix: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patient.patientInformation
            .generalPractitionerHonorificSuffix,
      ),
      general_practitioner_housenumber: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      general_practitioner_job_title: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patient.patientInformation.generalPractitionerJobTitle,
      ),
      general_practitioner_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.lastName,
      ),
      general_practitioner_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.patientForms.generalForm.sectionI.gpName,
      ),
      general_practitioner_phone_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.gpPhoneNumber,
      ),
      general_practitioner_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      general_practitioner_zipcode: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.gpZipCode,
      ),
      hospital_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      hospital_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      hospital_ik_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.ikNumber,
      ),
      hospital_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      hospital_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      hospital_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      insurance_ik_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.ikNumber,
      ),
      insurance_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.insuranceNumber,
      ),
      insured_details_application_insurance_benefits: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.questionNeedForCare,
      ),
      insured_details_guardian_1_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      insured_details_guardian_1_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.firstName,
      ),
      insured_details_guardian_1_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      insured_details_guardian_1_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.lastName,
      ),
      insured_details_guardian_1_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.gpPhoneNumber,
      ),
      insured_details_guardian_1_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      insured_details_guardian_1_type: toOntologyString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        ONTOLOGIES.GUARDIAN_TYPE,
        (translations) =>
          translations.patientForms.generalForm.sectionI.questionGuardian,
      ),
      insured_details_guardian_1_zipcode: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      insured_details_guardian_2_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      insured_details_guardian_2_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.firstName,
      ),
      insured_details_guardian_2_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      insured_details_guardian_2_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.lastName,
      ),
      insured_details_guardian_2_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.gpPhoneNumber,
      ),
      insured_details_guardian_2_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      insured_details_guardian_2_type: toOntologyString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        ONTOLOGIES.GUARDIAN_TYPE,
        (translations) =>
          translations.patientForms.generalForm.sectionI.questionGuardian,
      ),
      insured_details_guardian_2_zipcode: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      living_lift: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .livingSituationElevator,
      ),
      living_lift_description: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.livingSituationFloor,
      ),
      living_situation: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI.subtitleHomeCare,
        getLivingSituationValueLabel,
      ),
      living_situation_at_home: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .accessibilityAtHomeSituation,
        getLivingSituationAtHomeValueLabel,
      ),
      living_special: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .livingSituationSpecifications,
      ),
      patient_birthdate: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.dateOfBirth,
      ),
      patient_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      patient_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.firstName,
      ),
      patient_housenumber: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      patient_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.lastName,
      ),
      patient_phone_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.phone,
      ),
      patient_postfach: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.postfach,
      ),
      patient_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      patient_zipcode: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_2_admission_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.admissionPossible,
      ),
      prefered_clinic_2_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      prefered_clinic_2_contacted_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.rehabIntakeDate,
      ),
      prefered_clinic_2_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      prefered_clinic_2_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      prefered_clinic_2_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.contactInformation.phoneNumber,
      ),
      prefered_clinic_2_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_2_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      prefered_clinic_3_admission_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.admissionPossible,
      ),
      prefered_clinic_3_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      prefered_clinic_3_contacted_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.rehabIntakeDate,
      ),
      prefered_clinic_3_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      prefered_clinic_3_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      prefered_clinic_3_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.contactInformation.phoneNumber,
      ),
      prefered_clinic_3_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_3_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      prefered_clinic_4_admission_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.admissionPossible,
      ),
      prefered_clinic_4_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      prefered_clinic_4_contacted_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.rehabIntakeDate,
      ),
      prefered_clinic_4_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      prefered_clinic_4_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      prefered_clinic_4_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.contactInformation.phoneNumber,
      ),
      prefered_clinic_4_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_4_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      prefered_clinic_5_admission_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.admissionPossible,
      ),
      prefered_clinic_5_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      prefered_clinic_5_contacted_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.rehabIntakeDate,
      ),
      prefered_clinic_5_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      prefered_clinic_5_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      prefered_clinic_5_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.contactInformation.phoneNumber,
      ),
      prefered_clinic_5_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_5_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      prefered_clinic_admission_possible: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.admissionPossible,
      ),
      prefered_clinic_city: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.city,
      ),
      prefered_clinic_contacted: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.costCoverage,
      ),
      prefered_clinic_contacted_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.rehabIntakeDate,
      ),
      prefered_clinic_count: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.facility,
      ),
      prefered_clinic_house_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.number,
      ),
      prefered_clinic_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.institution.name,
      ),
      prefered_clinic_phone: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.contactInformation.phoneNumber,
      ),
      prefered_clinic_postal_code: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.zipcode,
      ),
      prefered_clinic_street: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.address.street,
      ),
      reason_for_reha_clinic: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .labelSpecialRequirements,
      ),
      retirement_earning_capacity_company: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .placeholderLabelInsuranceName,
      ),
      retirement_pension_applied: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .subtitleRetirementPension,
      ),
      retirement_pension_company: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .placeholderLabelInsuranceName,
      ),
      retirement_pension_earning_capacity_applied: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .subtitlePensionReducedEarning,
      ),
      site_id_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.careseeker.siteIdNumber,
      ),
      social_worker_fax_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.socialWorkerFax,
      ),
      social_worker_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.firstName,
      ),
      social_worker_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) => translations.people.lastName,
      ),
      social_worker_phone_number: passThrough(
        DOCS_FORM_NAMES.GKV_GENERAL,
        (translations) =>
          translations.patientForms.generalForm.sectionTwo.socialWorkerPhone,
      ),
    },
    [DOCS_FORM_NAMES.GKV_MEDICAL]: {
      admission_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.intakeDate,
      ),
      approved_by: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.insuranceApp.eligibilityTab.approvalFlow
            .authorisationQuestionTitle,
      ),
      complication_cause_cardiovascular: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.optionCardiovascular,
      ),
      complication_cause_cardiovascular_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.optionCardiovascular,
      ),
      complication_cause_maindiagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.relatedToDiagnosis,
      ),
      complication_cause_maindiagnosis_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.relatedToDiagnosis,
      ),
      complication_cause_other: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo
            .optionOtherComplication,
      ),
      complication_cause_other_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo
            .optionOtherComplication,
      ),
      complication_in_treatment_process: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.complications,
      ),
      discharge_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.dischargeDate,
      ),
      doctor_exam_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.doctorExamDateLabel,
      ),
      doctor_in_charge_in_hospital_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) => translations.people.firstName,
      ),
      doctor_in_charge_in_hospital_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) => translations.people.lastName,
      ),
      doctor_in_charge_in_hospital_phone: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.phoneDoctor,
      ),
      early_rehabilitation_measures: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.earlyRehabMeasures,
      ),
      gender: toOntologyString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        ONTOLOGIES.GENDER,
        (translations) =>
          translations.patientForms.drvGeneralForm.sectionTwo.gender,
      ),
      hospital_ik_number: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) => translations.people.ikNumber,
      ),
      icd_code_1_code: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.icdCode,
      ),
      icd_code_1_diagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.diagnosisDescription,
      ),
      icd_code_1_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.cause,
      ),
      icd_code_1_side_localization_primary: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.sideLocalization,
      ),
      icd_code_2_code: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.icdCode,
      ),
      icd_code_2_diagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.diagnosisDescription,
      ),
      icd_code_2_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.cause,
      ),
      icd_code_2_side_localization_primary: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.sideLocalization,
      ),
      icd_code_3_code: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.icdCode,
      ),
      icd_code_3_diagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.diagnosisDescription,
      ),
      icd_code_3_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.cause,
      ),
      icd_code_3_side_localization_primary: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.sideLocalization,
      ),
      icd_code_4_code: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.icdCode,
      ),
      icd_code_4_diagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.diagnosisDescription,
      ),
      icd_code_4_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.cause,
      ),
      icd_code_4_side_localization_primary: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.sideLocalization,
      ),
      icd_code_5_code: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.icdCode,
      ),
      icd_code_5_diagnosis: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.diagnosisDescription,
      ),
      icd_code_5_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.cause,
      ),
      icd_code_5_side_localization_primary: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.sideLocalization,
      ),
      infections: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.typeOfGerms,
      ),
      infections_state: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.germs,
      ),
      insurance_ik_number: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) => translations.people.ikNumber,
      ),
      insurance_number: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.insuranceNumber,
      ),
      interim_prosthesis_fitted: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree.prosthesis,
      ),
      internal_complications_hide_error: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.complications,
      ),
      limited_capacity: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree.resilience,
      ),
      operation_1_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryDate,
      ),
      operation_1_ops: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryOps,
      ),
      operation_1_wound_closed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.wound,
      ),
      operation_2_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryDate,
      ),
      operation_2_ops: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryOps,
      ),
      operation_2_wound_closed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.wound,
      ),
      operation_3_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryDate,
      ),
      operation_3_ops: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryOps,
      ),
      operation_3_wound_closed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.wound,
      ),
      operation_4_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryDate,
      ),
      operation_4_ops: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryOps,
      ),
      operation_4_wound_closed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.wound,
      ),
      operation_5_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryDate,
      ),
      operation_5_ops: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.surgeryOps,
      ),
      operation_5_wound_closed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.wound,
      ),
      other_treatments: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.otherTreatments,
      ),
      patient_birthdate: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.generalForm.insuredPerson.dateOfBirth,
      ),
      patient_body_height: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.bodyHeight,
      ),
      patient_body_weight: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.bodyWeight,
      ),
      patient_first_name: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.firstName,
      ),
      patient_last_name: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.lastName,
      ),
      recommended_reha_type: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive.typeOfRehab,
        getTypeOfRehabValueLabel,
      ),
      recommended_reha_type_reason: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo
            .complicationsDescriptionLabel,
      ),
      reha_capacity: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree
            .musculoskeletalSystem,
        getRehaCapacityValueLabel,
      ),
      reha_capacity_early_rehab_criteria: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree.earlyRehabIndex,
      ),
      reha_capacity_other: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree.specifics,
      ),
      reha_capacity_partial: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionThree
            .musculoskeletalSystem,
      ),
      rehab_admission_outside_two_weeks_explanation: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.medicalReasons,
      ),
      rehab_admission_outside_two_weeks_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.earliestDatePossible,
      ),
      rehab_admission_type: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.generalForm.sectionI
            .accessibilityGuardianType,
        getRehabAdmissionValueLabel,
      ),
      rehab_admission_within_two_weeks_start_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionOne.earliestDatePossible,
      ),
      rehab_directive_state: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive.rehabRl,
      ),
      rehab_directive_type: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive
            .rehabDirectiveTypeLabel,
        getRehabDirectiveTypeValueLabel,
      ),
      rehab_focus: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive.rehabFocus,
        getRehabFocusValueLabel,
      ),
      rehab_focus_other_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive.otherTextfieldLabel,
      ),
      rehabilitation_goals: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFour.rehabGoals,
      ),
      require_accompanying_person: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.accompanyingPerson,
      ),
      singer_assessment_concentration: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .taskConcentration,
      ),
      singer_assessment_date: toDateString(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .creationDate,
      ),
      singer_assessment_domestic_life_household: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .domesticLifeHousehold,
      ),
      singer_assessment_dressing_and_undressing: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyDress,
      ),
      singer_assessment_food: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyEat,
      ),
      singer_assessment_hygiene: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyHygiene,
      ),
      singer_assessment_listening: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .communicationListening,
      ),
      singer_assessment_orientation: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .taskOrientation,
      ),
      singer_assessment_planning: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .taskPlanning,
      ),
      singer_assessment_reading: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile.learnRead,
      ),
      singer_assessment_shower: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyShower,
      ),
      singer_assessment_social_interaction: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .socialInteraction,
      ),
      singer_assessment_stairs: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .mobilityStairs,
      ),
      singer_assessment_stool: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyStool,
      ),
      singer_assessment_talking: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .communicationTalk,
      ),
      singer_assessment_toilet_use: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyToilet,
      ),
      singer_assessment_transfer: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .mobilityTransfer,
      ),
      singer_assessment_urination: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .selfsufficiencyUrine,
      ),
      singer_assessment_walking: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .mobilityWalk,
      ),
      singer_assessment_wheelchair: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile
            .mobilityWheelchair,
      ),
      singer_assessment_writing: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patient.medicalDiagnosis.singerPatientProfile.learnWrite,
      ),
      special_requirements: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix
            .specialRequirementsSelectOptionsLabel,
      ),
      special_requirements_decubitus_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.decubitus,
      ),
      special_requirements_dialysis_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.dialysis,
      ),
      special_requirements_other_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionFive.otherTextfieldLabel,
      ),
      special_requirements_relatives_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.relatives,
      ),
      special_requirements_required: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.specialRequirements,
      ),
      special_requirements_weight_bed_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.impairment,
      ),
      surgery_performed: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionTwo.performedTreatments,
      ),
      transport_type: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.transportation,
        getTransportTypeValueLabel,
      ),
      transport_type_ambulance_description: passThrough(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix
            .ambulanceDescriptionLabel,
      ),
      transport_type_ambulance_specification: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.taxiOptionLabel,
        getTransportSubtypeValueLabel,
      ),
      transport_type_taxi_specification: toTranslatedEnum(
        DOCS_FORM_NAMES.GKV_MEDICAL,
        (translations) =>
          translations.patientForms.medicalForm.sectionSix.taxiOptionLabel,
        getTransportSubtypeValueLabel,
      ),
    },
    [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: {
      aftercare_application_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .applicationDate,
      ),
      aftercare_application_explanation: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .textFieldDescription,
      ),
      aftercare_application_submitted: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .applicationSubmitted,
      ),
      aftercare_care_level_aftercare_required: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .afterCareRequired,
      ),
      aftercare_care_level_applied: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .careLevelApplied,
      ),
      aftercare_care_level_applied_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .careLevelAppliedDate,
      ),
      aftercare_care_level_available: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC.careLevel,
      ),
      aftercare_care_level_explanation: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC
            .textFieldLabel,
      ),
      aftercare_medical_rehab: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC.medicalRehab,
      ),
      aftercare_mobile: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC.mobileCare,
      ),
      aftercare_shortterm_care: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionC.shorttermCare,
      ),
      aftercare_start_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionF
            .dateFieldLabel,
      ),
      care_necessity: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionF.title,
      ),
      clinic_ik_number: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalIkNumber,
      ),
      clinic_location_city: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalCity,
      ),
      clinic_location_street: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalStreet,
      ),
      clinic_location_street_number: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalStreetNumber,
      ),
      clinic_location_zipcode: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalZipCode,
      ),
      clinic_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .hospitalName,
      ),
      handover_to_insurance_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) => translations.patientForms.dateField.title,
      ),
      insurance_form_sent_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionD
            .dateFieldLabel,
      ),
      internet_portal_used: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionE.title,
      ),
      legal_representative_consent_provided: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .confirmedWithLegalRep,
      ),
      legal_representative_first_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepFirstName,
      ),
      legal_representative_last_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepName,
      ),
      legal_representative_location_city: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepCity,
      ),
      legal_representative_location_street: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepStreet,
      ),
      legal_representative_location_street_number: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepStreetNumber,
      ),
      legal_representative_location_zipcode: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .legalRepZipCode,
      ),
      patient_date_of_birth: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientBirthDate,
      ),
      patient_first_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientFirstName,
      ),
      patient_insurance_number: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientInsuranceNumber,
      ),
      patient_last_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientName,
      ),
      patient_location_city: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientCity,
      ),
      patient_location_house_number: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientStreetNumber,
      ),
      patient_location_street: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientStreet,
      ),
      patient_location_zipcode: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .patientZipCode,
      ),
      prior_treatment_end_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .releaseDate,
      ),
      prior_treatment_hospital_id: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay.id,
      ),
      prior_treatment_start_date: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .intakeDate,
      ),
      request_list: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionE
            .requestListTitle,
      ),
      social_worker_email: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .socialWorkerEmail,
      ),
      social_worker_fax: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .socialWorkerFax,
      ),
      social_worker_first_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .socialWorkerFirstName,
      ),
      social_worker_last_name: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .socialWorkerName,
      ),
      social_worker_telephone: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.contactInformation
            .socialWorkerPhone,
      ),
      transitional_care_department_code: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .departmentCode,
      ),
      transitional_care_description: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .textFieldLabel,
      ),
      transitional_care_end_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .endDateTransitionalCare,
      ),
      transitional_care_location_id: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .locationId,
      ),
      transitional_care_patient_confirmation: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .toggleConfirmedWithPatient,
      ),
      transitional_care_patient_confirmation_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .dateFieldLabel,
      ),
      transitional_care_patient_confirmation_explanation: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .textFieldExplanationLabel,
      ),
      transitional_care_start_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.hospitalStay
            .startDateTransitionalCare,
      ),
      transitional_care_start_search_date: toDateString(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionA.dateLabel,
      ),
      transitional_care_suitable_providers_count: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .dropdownNumberOfProviders,
      ),
      transitional_care_suitable_providers_more_than_twenty: passThrough(
        DOCS_FORM_NAMES.TRANSITIONAL_CARE,
        (translations) =>
          translations.patientForms.transitionalCareForm.sectionB
            .toggleNumberOfProviders,
      ),
    },
  };
